function getConfiguratorOpenMode() {
  return window.EeConfiguratorGallery?.isOpenScrollMode?.() ?? window.scrollY > 200;
}

function dispatchConfiguratorLayerChange() {
  try {
    document.dispatchEvent(new Event('configurator:addon-layer-change'));
  } catch (_) {}
}

class ConfiguratorAddOns extends HTMLElement {
  constructor() {
    super();
    this.selectedAddOns = new Map();
    this.productForm = null;
    this.mainProductId = null;
    this.initialized = false;
    this.currentOpenMode = getConfiguratorOpenMode();
    this.viewer = null;

    this.handleAddOnChange = this.handleAddOnChange.bind(this);
    this.init = this.init.bind(this);
    this.syncAllSelectedLayers = this.syncAllSelectedLayers.bind(this);
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.init());
    } else {
      setTimeout(() => this.init(), 100);
    }
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    this.productForm =
      this.closest("ee-configurator-product-info")?.querySelector(
        "ee-product-form"
      ) || document.querySelector("ee-product-form");
    this.mainProductId =
      this.closest("ee-configurator-product-info")?.dataset.productId ||
      document.querySelector("ee-configurator-product-info")?.dataset.productId;

    this.configuratorInfo =
      this.closest("ee-configurator-product-info") ||
      document.querySelector("ee-configurator-product-info");

    const allAddOnComponents = document.querySelectorAll(
      "configurator-add-ons"
    );
    this.addOnInputs = [];

    allAddOnComponents.forEach((component) => {
      const inputs = component.querySelectorAll(
        'input[type="checkbox"], input[type="radio"]'
      );
      inputs.forEach((input) => {
        if (
          !this.addOnInputs.find(
            (i) => i.name === input.name && i.id === input.id
          )
        ) {
          this.addOnInputs.push(input);
        }
      });
    });

    if (this.addOnInputs.length === 0) {
      this.addOnInputs = Array.from(
        document.querySelectorAll(
          'configurator-add-ons input[data-product-id], configurator-add-ons input[name^="addon-"]'
        )
      );
    }

    this.setupAddOnListeners();

    this.initializeSelectedState();

    this.setupVariantChangeListener();

    this.setupGlobalClickListener();

    this.setupOverrideImagesScrollListener();
    this.setupShadeLayerListeners();
    this.viewer = this.getViewer();

    try {
      document.dispatchEvent(new CustomEvent('configurator-addons-ready'));
    } catch (_) {}
  }

  getViewer() {
    return (
      document.querySelector('[id^="GalleryViewer"]') ||
      document.querySelector('[id^="Slider-Gallery"]')?.closest('slider-component')
    );
  }

  getOpenMode() {
    return getConfiguratorOpenMode();
  }

  /** One visible border per add-on card — avoids ::after + .add-border-3px stack. */
  setAddOnCardSelected(cardContainer, isSelected) {
    if (!cardContainer) return;
    const border = cardContainer.querySelector("#configurator-card-border");
    const wrapper = cardContainer.closest(".add-on-wrapper-flex");
    if (wrapper) wrapper.classList.remove("selected", "add-border-3px");

    if (isSelected) {
      cardContainer.classList.add("selected");
      if (border) border.classList.add("add-border-3px");
    } else {
      cardContainer.classList.remove("selected");
      if (border) border.classList.remove("add-border-3px");
    }
  }

  hasClosedLayerForMedia(viewer, mediaID) {
    if (!viewer || !mediaID) return false;
    return !!viewer.querySelector(
      `[data-add-on-media-id="${mediaID}"][data-open-image="false"]`
    );
  }

  applyFullToteStacking() {
    const toteInput = document.querySelector(
      'configurator-add-ons input[data-item-handle="full-tote-upgrade"]'
    );
    const enable = !!(toteInput && toteInput.checked);
    document.querySelectorAll('[data-sel-open-image]').forEach((item) => {
      const img = item.querySelector('img');
      if (enable) {
        item.setAttribute('data-sel-open-image', 'true');
        if (img) img.classList.add('z-index-custom');
      } else {
        item.setAttribute('data-sel-open-image', 'false');
        if (img) img.classList.remove('z-index-custom');
      }
    });
  }

  normalizeShade(value) {
    return this.normalizeMatchKey(value);
  }

  /** Normalize shade names and variant titles for stable equality checks. */
  normalizeMatchKey(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s*\/\s*/g, ' / ')
      .replace(/\s+/g, ' ');
  }

  getSelectedShade() {
    const form =
      document.querySelector('ee-product-form, configurator-product-form');
    if (form && form.getAttribute('data-swatch-targeted') !== 'true') return '';
    const fromForm = form && form.getAttribute('data-shade-name');
    if (fromForm && String(fromForm).trim()) return String(fromForm).trim();
    const vr = document.querySelector('ee-variant-selects, variant-radios');
    if (vr) {
      const inputs = vr.querySelectorAll('input[type="radio"]:checked');
      for (const inp of inputs) {
        const name = (inp.getAttribute('name') || '').toLowerCase();
        if (/shade|swatch/i.test(name) && inp.value) return inp.value;
      }
    }
    return '';
  }

  /**
   * Selected main-product variant title (e.g. "White Oak / Not Included").
   * Used to match add_on_product_variants.matching_variant_name.
   */
  getSelectedVariantTitle() {
    const info =
      this.configuratorInfo ||
      document.querySelector('ee-configurator-product-info');
    const variant =
      info?.currentVariant ||
      (typeof info?.getSelectedVariant === 'function'
        ? info.getSelectedVariant()
        : null);

    if (variant?.title && String(variant.title).trim()) {
      return String(variant.title).trim();
    }
    if (Array.isArray(variant?.options) && variant.options.length) {
      return variant.options.filter(Boolean).join(' / ');
    }

    const vr = document.querySelector('ee-variant-selects, variant-radios');
    if (vr) {
      const values = [];
      vr.querySelectorAll('fieldset').forEach((fieldset) => {
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        if (checked?.value) values.push(checked.value);
      });
      if (values.length) return values.join(' / ');
    }
    return '';
  }

  /**
   * Match keys for add_on_product_variants layers.
   * Prefer selected variant title; keep shade/swatch key for existing behavior.
   */
  getAddOnVariantMatchKeys() {
    const keys = [];
    const title = this.getSelectedVariantTitle();
    if (title) keys.push(this.normalizeMatchKey(title));
    const shade = this.getSelectedShade();
    if (shade) {
      const shadeKey = this.normalizeMatchKey(shade);
      if (!keys.includes(shadeKey)) keys.push(shadeKey);
    }
    return keys;
  }

  layerMatchesVariantKey(layerEl, matchKeys) {
    if (!layerEl || !matchKeys?.length) return false;
    const raw = layerEl.dataset.addOnShadeMatch;
    if (raw == null || String(raw).trim() === '') return false;
    return matchKeys.includes(this.normalizeMatchKey(raw));
  }

  getAddOnMediaId(input) {
    return (
      input.dataset.addOnId ||
      input.getAttribute('data-add-on-id') ||
      input.dataset.variantId ||
      input.getAttribute('data-variant-id') ||
      ''
    );
  }

  setLayerState(mediaID, isChecked, viewer, forceOpenMode) {
    if (!viewer || !mediaID) return;
    const activeMedia = viewer.querySelectorAll(
      `[data-add-on-media-id="${mediaID}"]`
    );
    const openMode =
      typeof forceOpenMode === 'boolean' ? forceOpenMode : this.getOpenMode();
    let targetOpenState = openMode ? 'true' : 'false';
    // Add-ons like Full Tote may only have open-state assets (no closed layer).
    if (
      isChecked &&
      targetOpenState === 'false' &&
      !this.hasClosedLayerForMedia(viewer, mediaID)
    ) {
      targetOpenState = 'true';
    }
    const matchKeys = this.getAddOnVariantMatchKeys();
    const titleKey = this.normalizeMatchKey(this.getSelectedVariantTitle());
    const shadeKey = this.normalizeMatchKey(this.getSelectedShade());

    // Prefer variant-title matches from add_on_product_variants; fall back to shade.
    const preferredKeys = titleKey
      ? [titleKey]
      : shadeKey
        ? [shadeKey]
        : [];
    let matchingVariantLayers = preferredKeys.length
      ? [...activeMedia].filter(
          (el) =>
            el.dataset.openImage === targetOpenState &&
            this.layerMatchesVariantKey(el, preferredKeys)
        )
      : [];
    if (!matchingVariantLayers.length && shadeKey && titleKey) {
      matchingVariantLayers = [...activeMedia].filter(
        (el) =>
          el.dataset.openImage === targetOpenState &&
          this.layerMatchesVariantKey(el, [shadeKey])
      );
    }
    // If neither title nor preferred shade found, allow any configured match key.
    if (!matchingVariantLayers.length && matchKeys.length) {
      matchingVariantLayers = [...activeMedia].filter(
        (el) =>
          el.dataset.openImage === targetOpenState &&
          this.layerMatchesVariantKey(el, matchKeys)
      );
    }
    const hasVariantMatch = matchingVariantLayers.length > 0;
    const activeMatchKeys = hasVariantMatch
      ? [
          ...new Set(
            matchingVariantLayers
              .map((el) => this.normalizeMatchKey(el.dataset.addOnShadeMatch))
              .filter(Boolean)
          ),
        ]
      : [];

    activeMedia.forEach((item) => {
      const isTargetState = item.dataset.openImage === targetOpenState;
      if (!isChecked || !isTargetState) {
        item.classList.remove('is-active');
        return;
      }
      const shadeMatchRaw = item.dataset.addOnShadeMatch;
      const isVariantMatchLayer =
        shadeMatchRaw != null && String(shadeMatchRaw).trim() !== '';
      const isOverrideLayer = item.dataset.overrideImage === 'true';

      if (targetOpenState === 'true') {
        if (isVariantMatchLayer) {
          if (
            hasVariantMatch &&
            this.layerMatchesVariantKey(item, activeMatchKeys)
          ) {
            item.classList.add('is-active');
          } else {
            item.classList.remove('is-active');
          }
          return;
        }
        if (isOverrideLayer) {
          if (hasVariantMatch) {
            item.classList.remove('is-active');
          } else {
            item.classList.add('is-active');
          }
          return;
        }
        if (hasVariantMatch) {
          item.classList.remove('is-active');
        } else {
          item.classList.add('is-active');
        }
        return;
      }
      if (isVariantMatchLayer) {
        if (
          hasVariantMatch &&
          this.layerMatchesVariantKey(item, activeMatchKeys)
        ) {
          item.classList.add('is-active');
        } else {
          item.classList.remove('is-active');
        }
        return;
      }
      if (hasVariantMatch) {
        item.classList.remove('is-active');
      } else {
        item.classList.add('is-active');
      }
    });
  }

  syncAllSelectedLayers(forceOpenMode) {
    const viewer = this.getViewer();
    if (!viewer) return;
    const openMode =
      typeof forceOpenMode === 'boolean' ? forceOpenMode : this.getOpenMode();
    this.currentOpenMode = openMode;

    const checkedInputs = document.querySelectorAll(
      'configurator-add-ons input[type="checkbox"]:checked, configurator-add-ons input[type="radio"]:checked'
    );
    const mediaIds = new Set();
    checkedInputs.forEach((input) => {
      const mediaID = this.getAddOnMediaId(input);
      if (mediaID) mediaIds.add(String(mediaID));
    });

    mediaIds.forEach((mediaID) => {
      this.setLayerState(mediaID, true, viewer, openMode);
    });

    viewer.querySelectorAll('[data-add-on-media-id]').forEach((item) => {
      const mediaID = item.getAttribute('data-add-on-media-id');
      if (!mediaIds.has(String(mediaID))) {
        this.setLayerState(mediaID, false, viewer, openMode);
      }
    });

    this.applyFullToteStacking();
  }

  setupShadeLayerListeners() {
    ConfiguratorAddOns.ensureShadeLayerListeners();
  }

  static ensureShadeLayerListeners() {
    if (ConfiguratorAddOns._shadeListenersBound) return;
    ConfiguratorAddOns._shadeListenersBound = true;

    document.addEventListener('configurator:addon-layer-change', () => {
      const el = document.querySelector('configurator-add-ons');
      if (el?.syncAllSelectedLayers) el.syncAllSelectedLayers();
    });

    document.addEventListener('configurator:layer-mode-change', (event) => {
      const openMode = !!(event && event.detail && event.detail.openMode);
      const el = document.querySelector('configurator-add-ons');
      if (el?.syncAllSelectedLayers) el.syncAllSelectedLayers(openMode);
    });
  }

  setupAddOnListeners() {
    this.addOnInputs.forEach((input, index) => {
      const changeHandler = (event) => {
        this.handleAddOnChange(event);
      };

      const clickHandler = (event) => {
        event.stopPropagation();
      };

      input.removeEventListener("change", changeHandler);
      input.removeEventListener("click", clickHandler);

      input.addEventListener("change", changeHandler);
      input.addEventListener("click", clickHandler);

      input._changeHandler = changeHandler;
      input._clickHandler = clickHandler;
    });
  }

  handleAddOnChange(event) {
    const input = event.target;
    if (
      input.closest('.compulsory-locked') &&
      window.eeConfiguratorCompulsory?.currentCompulsoryAddOn &&
      input.getAttribute('data-add-on-id') ===
        window.eeConfiguratorCompulsory.currentCompulsoryAddOn &&
      !input.checked
    ) {
      input.checked = true;
      return;
    }
    const cardContainer = input.closest(".config-card-container");
    const addOnProductId = input.dataset.productId || input.value;
    const addOnVariantId = input.dataset.variantId;

    if (!cardContainer) {
      console.error("Card container not found for input:", input);
      return;
    }

    if (event._processed) {
      return;
    }
    event._processed = true;

    if (input.type === "checkbox") {
      if (input.checked) {
        this.setAddOnCardSelected(cardContainer, true);
        this.selectedAddOns.set(addOnVariantId, {
          productId: addOnProductId,
          variantId: addOnVariantId,
          input: input,
        });
      } else {
        this.setAddOnCardSelected(cardContainer, false);
        this.selectedAddOns.delete(addOnVariantId);
      }
    } else if (input.type === "radio") {
      const fieldset = input.closest("fieldset");
      if (fieldset) {
        fieldset.querySelectorAll(".config-card-container").forEach((card) => {
          this.setAddOnCardSelected(card, false);
        });
      }

      const groupName = input.name;
      Array.from(this.selectedAddOns.entries()).forEach(([key, value]) => {
        if (value.input.name === groupName) {
          this.selectedAddOns.delete(key);
        }
      });

      this.setAddOnCardSelected(cardContainer, true);
      this.selectedAddOns.set(addOnVariantId, {
        productId: addOnProductId,
        variantId: addOnVariantId,
        input: input,
      });
    }

    this.toggleOverrideImages(input, addOnVariantId);
    this.syncAllSelectedLayers(this.getOpenMode());
    dispatchConfiguratorLayerChange();

    this.dispatchEvent(
      new CustomEvent("addon-selection-changed", {
        bubbles: true,
        detail: {
          selectedAddOns: Array.from(this.selectedAddOns.values()),
          changedAddOn: {
            productId: addOnProductId,
            variantId: addOnVariantId,
            selected: input.checked,
          },
        },
      })
    );

    this.updateFormWithAddOns();

    this.updateAllOverrideImages();
  }

  toggleOverrideImages(input, variantId) {
    if (!getConfiguratorOpenMode()) return;

    this.currentVariant = this.getCurrentVariant();
    const images = document.querySelectorAll('[data-override-image]');
    const addOnInputs = document.querySelectorAll('[data-add-on-id]');

    images.forEach((image) => {
      let matchFound = false;

      addOnInputs.forEach((addOnInput) => {
        if (
          addOnInput.checked &&
          image.dataset.overrideVariantList &&
          image.dataset.overrideVariantList.includes(addOnInput.dataset.addOnId)
        ) {
          matchFound = true;
        }
      });

      if (
        !matchFound &&
        image.dataset.overrideVariantList &&
        this.currentVariant &&
        image.dataset.overrideVariantList.includes(this.currentVariant.id.toString())
      ) {
        matchFound = true;
      }

      if (
        !matchFound &&
        input?.checked &&
        image.dataset.overrideVariantList &&
        variantId &&
        image.dataset.overrideVariantList.includes(variantId.toString())
      ) {
        matchFound = true;
      }

      image.classList.toggle('visible', matchFound);
      image.classList.toggle('invisible', !matchFound);
    });
  }

  initializeSelectedState() {
    this.addOnInputs.forEach((input) => {
      if (input.checked) {
        const cardContainer = input.closest(".config-card-container");
        if (cardContainer) {
          this.setAddOnCardSelected(cardContainer, true);
          const addOnProductId = input.dataset.productId || input.value;
          const addOnVariantId = input.dataset.variantId;

          this.selectedAddOns.set(addOnVariantId, {
            productId: addOnProductId,
            variantId: addOnVariantId,
            input: input,
          });
        }
      }
    });

    if (this.selectedAddOns.size > 0) {
      this.updateFormWithAddOns();
    }

    this.updateAllOverrideImages();
    dispatchConfiguratorLayerChange();
  }

  setupVariantChangeListener() {
    const configuratorInfo =
      this.closest("ee-configurator-product-info") ||
      document.querySelector("ee-configurator-product-info");
    if (configuratorInfo) {
      configuratorInfo.addEventListener("configurator:variant-changed", () => {
        this.updateAllOverrideImages();
        // Re-resolve add_on_product_variants layers against the new variant title/shade.
        this.syncAllSelectedLayers();
        dispatchConfiguratorLayerChange();
      });
    }
  }

  updateFormWithAddOns() {
    if (!this.productForm) {
      console.error("Product form not found");
      return;
    }

    const form = this.productForm.querySelector("form");
    if (!form) {
      console.error("Form element not found");
      return;
    }

    form
      .querySelectorAll(".addon-variant-input, .addon-quantity-input")
      .forEach((input) => {
        input.remove();
      });

    if (this.selectedAddOns.size > 0) {
      let mainVariantInput = form.querySelector('input[name="id"]');
      let mainQuantityInput = form.querySelector('input[name="quantity"]');

      const mainVariantId =
        window.EeConfiguratorVariantSync?.getSelectedMainVariantId?.() ||
        mainVariantInput?.value;

      if (mainVariantId && window.EeConfiguratorVariantSync?.syncMainVariantInputs) {
        window.EeConfiguratorVariantSync.syncMainVariantInputs(form, mainVariantId);
      }

      if (mainVariantInput) {
        let itemsMainInput = form.querySelector('input[name="items[0][id]"]');
        if (!itemsMainInput) {
          itemsMainInput = document.createElement("input");
          itemsMainInput.type = "hidden";
          itemsMainInput.name = "items[0][id]";
          itemsMainInput.className = "product-variant-id";
          form.appendChild(itemsMainInput);
        }
        itemsMainInput.value = mainVariantId || mainVariantInput.value;

        mainVariantInput.style.display = "none";
        mainVariantInput.disabled = true;
      }

      if (mainQuantityInput) {
        let itemsQuantityInput = form.querySelector(
          'input[name="items[0][quantity]"]'
        );
        if (!itemsQuantityInput) {
          itemsQuantityInput = document.createElement("input");
          itemsQuantityInput.type = "hidden";
          itemsQuantityInput.name = "items[0][quantity]";
          form.appendChild(itemsQuantityInput);
        }
        itemsQuantityInput.value = mainQuantityInput.value || "1";

        mainQuantityInput.style.display = "none";
        mainQuantityInput.disabled = true;
      } else {
        let itemsQuantityInput = form.querySelector(
          'input[name="items[0][quantity]"]'
        );
        if (!itemsQuantityInput) {
          itemsQuantityInput = document.createElement("input");
          itemsQuantityInput.type = "hidden";
          itemsQuantityInput.name = "items[0][quantity]";
          itemsQuantityInput.value = "1";
          form.appendChild(itemsQuantityInput);
        }
      }

      let index = 1;
      this.selectedAddOns.forEach((addon, variantId) => {
        if (variantId) {
          const hiddenInput = document.createElement("input");
          hiddenInput.type = "hidden";
          hiddenInput.name = `items[${index}][id]`;
          hiddenInput.value = variantId;
          hiddenInput.className = "addon-variant-input";
          form.appendChild(hiddenInput);

          const quantityInput = document.createElement("input");
          quantityInput.type = "hidden";
          quantityInput.name = `items[${index}][quantity]`;
          quantityInput.value = "1";
          quantityInput.className = "addon-quantity-input";
          form.appendChild(quantityInput);

          index++;
        }
      });

    } else {
      const itemsMainInput = form.querySelector('input[name="items[0][id]"]');
      const mainVariantInput = form.querySelector('input[name="id"]');

      if (itemsMainInput && mainVariantInput) {
        mainVariantInput.value = itemsMainInput.value;
        mainVariantInput.style.display = "";
        mainVariantInput.disabled = false;
        itemsMainInput.remove();
      }

      const itemsQuantityInput = form.querySelector(
        'input[name="items[0][quantity]"]'
      );
      const mainQuantityInput = form.querySelector('input[name="quantity"]');

      if (itemsQuantityInput) {
        if (mainQuantityInput) {
          mainQuantityInput.value = itemsQuantityInput.value;
          mainQuantityInput.style.display = "";
          mainQuantityInput.disabled = false;
        }
        itemsQuantityInput.remove();
      }
    }
  }

  setupGlobalClickListener() {
    document.addEventListener(
      "change",
      (event) => {
        const input = event.target;
        if (
          input.closest("configurator-add-ons") &&
          input.matches('input[type="checkbox"], input[type="radio"]') &&
          (input.dataset.productId || input.name.startsWith("addon-"))
        ) {
          if (!event._processed) {
            this.handleAddOnChange(event);
          }
        }
      },
      true
    );
  }

  setupOverrideImagesScrollListener() {
    const debounce = (func, wait) => {
      let timeout;
      return function (...args) {
        const context = this;
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(context, args), wait);
      };
    };

    const scrollHandler = () => {
      if (this.selectedAddOns.size > 0) {
        this.updateAllOverrideImages();
      }
    };

    this.overrideImagesScrollHandler = debounce(scrollHandler, 50);

    window.addEventListener("scroll", this.overrideImagesScrollHandler);
  }

  getSelectedAddOns() {
    return Array.from(this.selectedAddOns.values());
  }

  clearSelections() {
    this.addOnInputs.forEach((input) => {
      input.checked = false;
      const cardContainer = input.closest(".config-card-container");
      if (cardContainer) {
        this.setAddOnCardSelected(cardContainer, false);
      }
    });

    this.selectedAddOns.clear();
    this.updateFormWithAddOns();

    this.updateAllOverrideImages();
  }

  getCurrentVariant() {
    if (!this.productForm) return null;

    const variantInput = this.productForm.querySelector(
      'input[name="id"], input[name="items[0][id]"]'
    );
    if (variantInput && variantInput.value) {
      return {
        id: parseInt(variantInput.value),
      };
    }
    const configuratorInfo =
      this.closest("ee-configurator-product-info") ||
      document.querySelector("ee-configurator-product-info");
    if (configuratorInfo && configuratorInfo.currentVariant) {
      return configuratorInfo.currentVariant;
    }

    return null;
  }

  updateAllOverrideImages() {
    if (!getConfiguratorOpenMode()) {
      document.querySelectorAll('[data-override-image]').forEach((image) => {
        image.classList.add('invisible');
        image.classList.remove('visible');
      });
      return;
    }

    this.currentVariant = this.getCurrentVariant();
    const images = document.querySelectorAll('[data-override-image]');
    const addOnInputs = document.querySelectorAll('[data-add-on-id]');

    images.forEach((image) => {
      let matchFound = false;

      addOnInputs.forEach((addOnInput) => {
        if (
          addOnInput.checked &&
          image.dataset.overrideVariantList &&
          image.dataset.overrideVariantList.includes(addOnInput.dataset.addOnId)
        ) {
          matchFound = true;
        }
      });

      if (
        !matchFound &&
        image.dataset.overrideVariantList &&
        this.currentVariant &&
        image.dataset.overrideVariantList.includes(this.currentVariant.id.toString())
      ) {
        matchFound = true;
      }

      image.classList.toggle('visible', matchFound);
      image.classList.toggle('invisible', !matchFound);
    });
  }
}

customElements.define("configurator-add-ons", ConfiguratorAddOns);
